import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error info:', errorInfo);
    
    // Update state with error info
    this.setState({
      error,
      errorInfo
    });

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
          <div className="container py-5">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="card border-0 shadow-lg">
                  <div className="card-body p-5 text-center">
                    <div className="mb-4">
                      <AlertTriangle className="text-warning mb-3" size={64} />
                    </div>
                    <h1 className="h2 fw-bold text-dark mb-3">
                      Oops! Something went wrong
                    </h1>
                    <p className="text-muted mb-4">
                      We encountered an unexpected error while loading this page. 
                      This could be due to a temporary issue or missing data.
                    </p>
                    
                    {process.env.NODE_ENV === 'development' && this.state.error && (
                      <div className="alert alert-danger text-start mb-4">
                        <h6 className="fw-bold mb-2">Error Details (Development Mode):</h6>
                        <pre className="small mb-2">
                          <code>{this.state.error.toString()}</code>
                        </pre>
                        {this.state.errorInfo && (
                          <details className="mt-2">
                            <summary className="fw-semibold">Component Stack</summary>
                            <pre className="small mt-2">
                              <code>{this.state.errorInfo.componentStack}</code>
                            </pre>
                          </details>
                        )}
                      </div>
                    )}

                    <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                      <button
                        onClick={this.handleRetry}
                        className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
                      >
                        <RefreshCw size={16} />
                        Try Again
                      </button>
                      <Link 
                        to="/" 
                        className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2"
                      >
                        <Home size={16} />
                        Go Home
                      </Link>
                    </div>

                    <div className="mt-4">
                      <p className="small text-muted">
                        If this problem persists, please{' '}
                        <Link to="/contact" className="text-primary">
                          contact our support team
                        </Link>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;